import React from "react";
import { errorToast, successToast } from "../../../../utils/toast.util";
import { DispatchType } from "../../../../interfaces/redux.interface";
import { useDispatch, useSelector } from "react-redux";
import {
  startLoading,
  stopLoading,
} from "../../../../redux/slices/WindowSlice";
import { changeRequestStatus } from "../../../../redux/slices/TuitionRequestSlice";
import { useNavigate } from "react-router-dom";
import { TuitionRequestInterface } from "../../../../interfaces/tuitionRequest.interface";
import { RootState } from "../../../../redux/store";

const RequestForm = ({
  requestDetails,
}: {
  requestDetails: TuitionRequestInterface;
}) => {
  const dispatch: DispatchType = useDispatch();
  const navigate = useNavigate();
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "approved":
        return "bg-green-100 text-green-800 border-green-300";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const handleSubmit = async (status: "approved" | "rejected") => {
    dispatch(startLoading());
    await dispatch(
      changeRequestStatus({
        id: requestDetails?._id,
        status,
        email: signedAdmin.email,
        role: signedAdmin.role,
        type: signedAdmin.type,
      })
    )
      .unwrap()
      .then((res: any) => {
        successToast("successfully tuition period created ");
        navigate("/tuition-requests");
      })
      .catch((err) => {
        errorToast("something went wrong");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

  return (
    <div className="space-y-6 mt-4">
      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-lg font-semibold text-gray-700">
            Request Status:
          </div>
          <span
            className={`px-4 py-2 rounded-full border-2 font-semibold text-sm ${getStatusBadgeColor(
              requestDetails?.adminStatus
            )}`}
          >
            {requestDetails?.adminStatus?.toUpperCase() || "UNKNOWN"}
          </span>
          {requestDetails?.isTRWithdrawnByParent && (
            <span className="px-4 py-2 rounded-full border-2 font-semibold text-sm bg-orange-100 text-orange-800 border-orange-300">
              WITHDRAWN BY PARENT
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500">
          Created: {formatDate(requestDetails?.createdAt)}
        </div>
      </div>

      {requestDetails?.isTRWithdrawnByParent && (
        <div className="bg-orange-50 border border-orange-300 text-orange-800 px-4 py-3 rounded-lg font-medium">
          Withdrawn by Parent — no admin action required
        </div>
      )}

      {/* Main Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Parent & Student Information */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-md border border-blue-200">
          <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
            <span className="text-2xl">👨‍👩‍👧</span>
            Parent & Student
          </h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-semibold text-blue-700">
                Parent:
              </span>
              <p className="text-gray-800 font-medium mt-1">
                {requestDetails?.parent?.name || "N/A"}
              </p>
            </div>
            <div>
              <span className="text-sm font-semibold text-blue-700">
                Student:
              </span>
              <p className="text-gray-800 font-medium mt-1">
                {requestDetails?.student?.name || "N/A"}
              </p>
            </div>
            <div className="flex gap-2 mt-4">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  requestDetails?.isFirst
                    ? "bg-green-200 text-green-800"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                {requestDetails?.isFirst ? "First Time" : "Returning"}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  requestDetails?.isTeacherChoosenByParent
                    ? "bg-purple-200 text-purple-800"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                {requestDetails?.isTeacherChoosenByParent
                  ? "Teacher Chosen"
                  : "No Preference"}
              </span>
            </div>
          </div>
        </div>

        {/* Preferred Teacher */}
        {requestDetails?.preferredTeacher && (
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl shadow-md border border-purple-200">
            <h3 className="text-xl font-bold text-purple-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">👨‍🏫</span>
              Preferred Teacher
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-semibold text-purple-700">
                  Name:
                </span>
                <p className="text-gray-800 font-medium mt-1">
                  {requestDetails?.preferredTeacher?.name || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm font-semibold text-purple-700">
                  Gender:
                </span>
                <p className="text-gray-800 font-medium mt-1 capitalize">
                  {requestDetails?.preferredTeacher?.gender || "N/A"}
                </p>
              </div>
              {requestDetails?.preferredTeacher?.about && (
                <div>
                  <span className="text-sm font-semibold text-purple-700">
                    About:
                  </span>
                  <p className="text-gray-700 text-sm mt-1 line-clamp-3">
                    {requestDetails?.preferredTeacher?.about}
                  </p>
                </div>
              )}
              {requestDetails?.preferredTeacher?.subjects?.length > 0 && (
                <div>
                  <span className="text-sm font-semibold text-purple-700">
                    Subjects:
                  </span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {requestDetails?.preferredTeacher?.subjects?.map(
                      (subject, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-200 text-purple-800 rounded text-xs font-medium"
                        >
                          {subject}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Teaching Set Information */}
        {requestDetails?.teachingSetId && (
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-md border border-green-200">
            <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">📚</span>
              Teaching Set
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-semibold text-green-700">
                  Board:
                </span>
                <p className="text-gray-800 font-medium mt-1">
                  {requestDetails?.teachingSetId?.board || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm font-semibold text-green-700">
                  Level:
                </span>
                <p className="text-gray-800 font-medium mt-1">
                  {requestDetails?.teachingSetId?.level?.replace("_", " ") ||
                    "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm font-semibold text-green-700">
                  Mode:
                </span>
                <p className="text-gray-800 font-medium mt-1">
                  {requestDetails?.teachingSetId?.teachingMode || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm font-semibold text-green-700">
                  Stream:
                </span>
                <p className="text-gray-800 font-medium mt-1">
                  {requestDetails?.teachingSetId?.subjectStream?.replace(
                    "_",
                    " "
                  ) || "N/A"}
                </p>
              </div>
              {requestDetails?.teachingSetId?.subjects?.length > 0 && (
                <div>
                  <span className="text-sm font-semibold text-green-700">
                    Subjects:
                  </span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {requestDetails?.teachingSetId?.subjects?.map(
                      (subject, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-green-200 text-green-800 rounded text-xs font-medium"
                        >
                          {subject}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Class Requirements */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl shadow-md border border-orange-200">
          <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center gap-2">
            <span className="text-2xl">📊</span>
            Class Requirements
          </h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-semibold text-orange-700">
                Required Classes:
              </span>
              <p className="text-gray-800 font-medium mt-1 text-lg">
                {requestDetails?.requiredNumberOfClasses || "N/A"}
              </p>
            </div>
            <div>
              <span className="text-sm font-semibold text-orange-700">
                Minimum Booking:
              </span>
              <p className="text-gray-800 font-medium mt-1">
                {requestDetails?.teachingSetId?.minimumClassBooking || "N/A"}{" "}
                classes
              </p>
            </div>
            {requestDetails?.preferredSubjects?.length > 0 && (
              <div>
                <span className="text-sm font-semibold text-orange-700">
                  Preferred Subjects:
                </span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {requestDetails?.preferredSubjects?.map((subject, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs font-medium"
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Discounts & Offers */}
        {requestDetails?.teachingSetId && (
          <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-xl shadow-md border border-pink-200">
            <h3 className="text-xl font-bold text-pink-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">💰</span>
              Discounts & Offers
            </h3>
            <div className="space-y-3">
              {requestDetails?.teachingSetId?.discountOnTotal?.isApply && (
                <div className="bg-white p-3 rounded-lg">
                  <span className="text-sm font-semibold text-pink-700">
                    Total Discount:
                  </span>
                  <p className="text-gray-800 font-medium mt-1">
                    {requestDetails?.teachingSetId?.discountOnTotal?.value || 0}
                    %
                  </p>
                </div>
              )}
              {requestDetails?.teachingSetId?.discountOrHikePerClassFee
                ?.isApply && (
                <div className="bg-white p-3 rounded-lg">
                  <span className="text-sm font-semibold text-pink-700">
                    Per Class Fee:
                  </span>
                  <p className="text-gray-800 font-medium mt-1">
                    {requestDetails?.teachingSetId?.discountOrHikePerClassFee
                      ?.value || 0}
                    %
                  </p>
                </div>
              )}
              {requestDetails?.teachingSetId?.noOfSessionFree?.isApply && (
                <div className="bg-white p-3 rounded-lg">
                  <span className="text-sm font-semibold text-pink-700">
                    Free Sessions:
                  </span>
                  <p className="text-gray-800 font-medium mt-1">
                    {requestDetails?.teachingSetId?.noOfSessionFree?.value || 0}{" "}
                    sessions
                  </p>
                </div>
              )}
              {!requestDetails?.teachingSetId?.discountOnTotal?.isApply &&
                !requestDetails?.teachingSetId?.discountOrHikePerClassFee
                  ?.isApply &&
                !requestDetails?.teachingSetId?.noOfSessionFree?.isApply && (
                  <p className="text-gray-600 text-sm">No discounts applied</p>
                )}
            </div>
          </div>
        )}

        {/* Teacher Status */}
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl shadow-md border border-indigo-200">
          <h3 className="text-xl font-bold text-indigo-800 mb-4 flex items-center gap-2">
            <span className="text-2xl">📋</span>
            Status Information
          </h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-semibold text-indigo-700">
                Teacher Status:
              </span>
              <p className="mt-1">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(
                    requestDetails?.teacherStatus
                  )}`}
                >
                  {requestDetails?.teacherStatus?.toUpperCase() || "N/A"}
                </span>
              </p>
            </div>
            <div>
              <span className="text-sm font-semibold text-indigo-700">
                Last Updated:
              </span>
              <p className="text-gray-800 font-medium mt-1 text-sm">
                {formatDate(requestDetails?.updatedAt)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Preferred Schedule */}
      {requestDetails?.preferredSchedule?.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-2xl">📅</span>
            Preferred Schedule
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {requestDetails?.preferredSchedule?.map((schedule, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border border-cyan-200"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-cyan-800">
                    {schedule.day}
                  </span>
                  <span className="text-sm text-gray-600">
                    {schedule.from} - {schedule.to}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {requestDetails?.adminStatus === "pending" &&
        !requestDetails?.isTRWithdrawnByParent && (
        <div className="flex flex-col sm:flex-row gap-4 mt-6 sticky bottom-0 bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <button
            onClick={() => handleSubmit("rejected")}
            className="flex-1 bg-red-500 text-white py-3 px-6 rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            ❌ Reject Request
          </button>
          <button
            onClick={() => handleSubmit("approved")}
            className="flex-1 bg-green-500 text-white py-3 px-6 rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            ✅ Approve Request
          </button>
        </div>
      )}
    </div>
  );
};

export default RequestForm;
